/**
 * Hook to check if the current user has an active (non-expired) subscription.
 * Used to gate practice actions (record, compare, play) for non-paid users.
 * Reads loggedInUser from localStorage (userId, uid, email); if empty, falls back
 * to Firebase Auth current user so subscriptions are found even when localStorage
 * wasn't set (e.g. WebView, new tab). Queries by userId and email (normalized to
 * lowercase) so docs are always found regardless of case.
 *
 * A child also inherits access from any parent they're linked to (via
 * parentChildLinks) who has their own active subscription, so premium features
 * unlock for the whole family once one parent subscribes.
 */
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from './firebaseConfig';
import { getParentsForUser } from './parentStudentFirestore';

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

const SUB_LOG = '[Subscriptions]';

interface SubscriptionDoc {
  id: string;
  productId: string;
  userId: string | null;
  expiresAt: Date | string | { toDate: () => Date } | { seconds: number };
}

function parseExpiresAt(expiresAt: SubscriptionDoc['expiresAt']): Date {
  if (expiresAt instanceof Date) return expiresAt;
  if (typeof expiresAt === 'string') return new Date(expiresAt);
  if (expiresAt && typeof (expiresAt as { toDate?: () => Date }).toDate === 'function') {
    return (expiresAt as { toDate: () => Date }).toDate();
  }
  if (expiresAt && typeof expiresAt === 'object' && 'seconds' in (expiresAt as object)) {
    return new Date((expiresAt as unknown as { seconds: number }).seconds * 1000);
  }
  return new Date(0);
}

/** Same as PracticePage/PaymentsPage: prefer userId, then uid, then email. Email normalized to lowercase for Firestore match. */
function getLoggedInUserIdAndEmail(stored: string | null): {
  userId: string | null;
  email: string | null;
} {
  if (!stored) return { userId: null, email: null };
  try {
    const parsed = JSON.parse(stored) as { userId?: string; uid?: string; email?: string };
    const userId = parsed?.userId ?? parsed?.uid ?? parsed?.email ?? null;
    const emailRaw = parsed?.email ?? null;
    const email =
      emailRaw && String(emailRaw).trim() !== '' ? String(emailRaw).trim().toLowerCase() : null;
    return {
      userId: userId && String(userId).trim() !== '' ? String(userId).trim() : null,
      email,
    };
  } catch {
    return { userId: null, email: null };
  }
}

export function useHasActiveSubscription(): {
  hasActiveSubscription: boolean;
  loading: boolean;
} {
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  // null = not resolved yet; [] = resolved, no linked parents
  const [parentEmails, setParentEmails] = useState<string[] | null>(null);

  useEffect(() => {
    const applyIdentity = (u: string | null, e: string | null, source: string) => {
      console.log(SUB_LOG, 'useHasActiveSubscription identity', {
        source,
        hasUserId: !!u,
        userIdLength: u?.length ?? 0,
        hasEmail: !!e,
        emailPrefix: e ? `${e.slice(0, 3)}***` : null,
      });
      setUserId(u);
      setEmail(e);
    };
    const stored = localStorage.getItem('loggedInUser');
    const fromStorage = getLoggedInUserIdAndEmail(stored);
    if (fromStorage.userId || fromStorage.email) {
      applyIdentity(fromStorage.userId, fromStorage.email, 'localStorage');
      return;
    }
    console.log(SUB_LOG, 'useHasActiveSubscription: no localStorage, waiting for Firebase Auth');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        applyIdentity(
          user.uid,
          user.email?.trim() ? user.email.trim().toLowerCase() : null,
          'Firebase Auth',
        );
      } else {
        applyIdentity(null, null, 'Firebase Auth (signed out)');
      }
    });
    return () => unsubscribe();
  }, []);

  // Resolve which parents (if any) this user is linked to as a child, so their
  // subscription can be checked too. Re-runs whenever the identity's email changes.
  useEffect(() => {
    if (!email) {
      setParentEmails([]);
      return;
    }
    let cancelled = false;
    getParentsForUser(email)
      .then((parents) => {
        if (cancelled) return;
        setParentEmails(parents.map((p) => p.email.trim().toLowerCase()).filter(Boolean));
      })
      .catch((err) => {
        console.warn(SUB_LOG, 'useHasActiveSubscription: failed to load linked parents', err);
        if (!cancelled) setParentEmails([]);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  useEffect(() => {
    if (!userId && !email) {
      console.log(
        SUB_LOG,
        'useHasActiveSubscription: no userId/email, setting hasActiveSubscription=false',
      );
      setLoading(false);
      setHasActiveSubscription(false);
      return;
    }
    // Wait for the linked-parents lookup so we know the full set of sources to
    // listen to before deciding "loading" is done.
    if (parentEmails === null) return;

    setLoading(true);
    const subsRef = collection(db, 'subscriptions');
    const docsBySource = new Map<string, SubscriptionDoc[]>();

    const sources: string[] = [];
    if (userId) sources.push('ownUserId');
    if (email) sources.push('ownEmail');
    parentEmails.forEach((pe) => sources.push(`parent:${pe}`));
    const gotSnapshot = new Set<string>();

    const recompute = (): void => {
      const merged: SubscriptionDoc[] = [];
      Array.from(docsBySource.values()).forEach((docs) => {
        docs.forEach((doc) => {
          if (!merged.some((d) => d.id === doc.id)) merged.push(doc);
        });
      });
      const sorted = merged.sort((a, b) => {
        const aTime = parseExpiresAt(a.expiresAt).getTime();
        const bTime = parseExpiresAt(b.expiresAt).getTime();
        return bTime - aTime;
      });
      const doc = sorted[0];
      if (doc) {
        const expiresAt = parseExpiresAt(doc.expiresAt);
        const active = expiresAt > new Date();
        console.log(SUB_LOG, 'useHasActiveSubscription result', {
          docId: doc.id,
          productId: doc.productId,
          expiresAt: expiresAt.toISOString(),
          active,
        });
        setHasActiveSubscription(active);
      } else {
        console.log(SUB_LOG, 'useHasActiveSubscription result: no subscription doc found');
        setHasActiveSubscription(false);
      }
      if (sources.every((s) => gotSnapshot.has(s))) setLoading(false);
    };

    const unsubscribers: Array<() => void> = [];

    if (userId) {
      const qUserId = query(subsRef, where('userId', '==', userId), limit(10));
      unsubscribers.push(
        onSnapshot(
          qUserId,
          (snap) => {
            docsBySource.set(
              'ownUserId',
              snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
            gotSnapshot.add('ownUserId');
            console.log(SUB_LOG, 'useHasActiveSubscription Firestore by userId', {
              count: docsBySource.get('ownUserId')?.length ?? 0,
            });
            recompute();
          },
          (err) => {
            console.warn(SUB_LOG, 'useHasActiveSubscription userId listener error', err);
            gotSnapshot.add('ownUserId');
            recompute();
          },
        ),
      );
    }

    if (email) {
      const emailLower = email.toLowerCase();
      const qEmail = query(subsRef, where('email', '==', emailLower), limit(10));
      unsubscribers.push(
        onSnapshot(
          qEmail,
          (snap) => {
            docsBySource.set(
              'ownEmail',
              snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
            gotSnapshot.add('ownEmail');
            console.log(SUB_LOG, 'useHasActiveSubscription Firestore by email', {
              count: docsBySource.get('ownEmail')?.length ?? 0,
            });
            recompute();
          },
          (err) => {
            console.warn(SUB_LOG, 'useHasActiveSubscription email listener error', err);
            gotSnapshot.add('ownEmail');
            recompute();
          },
        ),
      );
    }

    parentEmails.forEach((parentEmail) => {
      const sourceKey = `parent:${parentEmail}`;
      const qParent = query(subsRef, where('email', '==', parentEmail), limit(10));
      unsubscribers.push(
        onSnapshot(
          qParent,
          (snap) => {
            docsBySource.set(
              sourceKey,
              snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
            gotSnapshot.add(sourceKey);
            console.log(SUB_LOG, 'useHasActiveSubscription Firestore by parent email', {
              parentEmail,
              count: docsBySource.get(sourceKey)?.length ?? 0,
            });
            recompute();
          },
          (err) => {
            console.warn(SUB_LOG, 'useHasActiveSubscription parent email listener error', err);
            gotSnapshot.add(sourceKey);
            recompute();
          },
        ),
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userId, email, parentEmails]);

  return { hasActiveSubscription, loading };
}
